import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Plus } from "lucide-react";
import { PaginatedGrid } from "@/components/ui/paginated-grid";
import { CampaignCard } from "./CampaignCard";
import { Campaign } from "@/types/campaign";
import { getCampaignStatus } from "@/utils/listingUtils";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";

interface CampaignGridProps {
  campaigns: Campaign[];
  onCreateNew: () => void;
  onCampaignClick?: (campaign: Campaign) => void;
  vehicleCounts?: Record<string, number>;
}

function normalizeCampaignType(type: string | undefined): string {
  return String(type ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
}

export const CampaignGrid = ({
  campaigns,
  onCreateNew,
  onCampaignClick,
  vehicleCounts = {},
}: CampaignGridProps) => {
  const { language } = useLanguage();
  const L = (adminLabels(language) as any)?.leadboxListings ?? {};
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [activeMainTab, setActiveMainTab] = useState<string>("all");
  const [activeStatusTab, setActiveStatusTab] = useState<string>("all");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 0);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  // Keep focus on search input
  const searchInputRef = useRef<HTMLInputElement>(null);

  const filteredCampaigns = useMemo(() => {
    const filtered = campaigns.filter((campaign) => {
      const matchesSearch =
        campaign.name
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase()) ||
        campaign.description
          .toLowerCase()
          .includes(debouncedSearchTerm.toLowerCase());

      const campaignType = normalizeCampaignType(campaign.type);
      const matchesMainTab =
        activeMainTab === "all" || campaignType === activeMainTab;

      // Use dynamic status calculation instead of static status field
      const campStatus = getCampaignStatus(campaign);
      const matchesStatusTab =
        activeStatusTab === "all"
          ? true
          : activeStatusTab === "active"
            ? campStatus === "active"
            : activeStatusTab === "upcoming"
              ? campStatus === "upcoming"
              : activeStatusTab === "completed"
                ? campStatus === "completed"
                : activeStatusTab === "cancelled"
                  ? campStatus === "cancelled"
                  : true;
      return matchesSearch && matchesMainTab && matchesStatusTab;
    });

    return filtered;
  }, [campaigns, debouncedSearchTerm, activeMainTab, activeStatusTab]);

  // Keep focus on search input when filteredCampaigns changes
  useEffect(() => {
    if (
      searchInputRef.current &&
      document.activeElement !== searchInputRef.current
    ) {
      searchInputRef.current.focus();
    }
  }, [filteredCampaigns]);

  const renderCampaignCard = (campaign: Campaign) => {
    return (
      <CampaignCard
        key={campaign._id}
        campaign={campaign}
        onCampaignClick={onCampaignClick}
        vehicleCount={
          campaign.totalListings ?? vehicleCounts[campaign._id] ?? 0
        }
      />
    );
  };

  return (
    <div className="space-y-4">
      <Tabs
        value={activeMainTab}
        onValueChange={setActiveMainTab}
        className="w-full"
      >
        <TabsList className="grid w-auto grid-cols-3 backdrop-blur">
          <TabsTrigger value="all">{L.all}</TabsTrigger>
          <TabsTrigger value="auction">{L.auctionsTab}</TabsTrigger>
          <TabsTrigger value="direct-purchase">{L.directPurchase}</TabsTrigger>
        </TabsList>
      </Tabs>

      <Tabs
        value={activeStatusTab}
        onValueChange={setActiveStatusTab}
        className="w-full"
      >
        <div className="flex items-center justify-center">
          <TabsList className="grid w-auto grid-cols-5 backdrop-blur">
            <TabsTrigger value="all">{L.all}</TabsTrigger>
            <TabsTrigger value="active">{L.activeTab}</TabsTrigger>
            <TabsTrigger value="upcoming">{L.upcomingTab}</TabsTrigger>
            <TabsTrigger value="completed">{L.completedTab}</TabsTrigger>
            <TabsTrigger value="cancelled">{L.cancelledTab}</TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {/* Content outside nested TabsContent — filter driven by tab state only */}
      <div className="mt-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  ref={searchInputRef}
                  key="campaigns-search"
                  placeholder={L.searchCampaignsPlaceholder}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          <Button
            onClick={onCreateNew}
            size="sm"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {L.createCampaign}
          </Button>
        </div>

        <div className="text-sm text-primary/60">
          {L.campaignsFound
            ?.replace("{count}", String(filteredCampaigns.length))
            .replace(
              "{plural}",
              filteredCampaigns.length !== 1
                ? language === "en"
                  ? "s"
                  : "er"
                : "",
            )}
          {debouncedSearchTerm && ` för "${debouncedSearchTerm}"`}
        </div>

        <PaginatedGrid
          key={`${activeMainTab}-${activeStatusTab}`}
          data={filteredCampaigns}
          renderItem={renderCampaignCard}
          itemsPerPage={12}
          className="mt-4"
          emptyMessage={
            debouncedSearchTerm ? L.noCampaignsWithFilters : L.noCampaignsYet
          }
          campaignCards={true}
        />
      </div>
    </div>
  );
};
